import React, { useMemo } from "react";
import { ConfigProvider, theme as antdTheme } from "antd";
import { createTheme, ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { useTheme } from "../context/ThemeContext";

/**
 * Bridges the app ThemeContext to Ant Design and MUI so that every
 * library-rendered surface (modals, dropdowns, selects, date pickers,
 * tooltips, popovers, message/notification portals) follows dark mode.
 */
export function AppThemeProviders({ children }) {
  const { isDark } = useTheme();

  const antdConfig = useMemo(
    () => ({
      algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
      token: {
        colorPrimary: "#3b82f6",
        borderRadius: 8,
        fontFamily: "Poppins, sans-serif",
        ...(isDark
          ? {
              colorBgContainer: "#1e293b",
              colorBgElevated: "#1e293b",
              colorBgLayout: "#0f172a",
              colorBorder: "#334155",
            }
          : {}),
      },
    }),
    [isDark]
  );

  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: isDark ? "dark" : "light",
          primary: { main: "#3b82f6" },
          ...(isDark
            ? {
                background: { default: "#0f172a", paper: "#1e293b" },
                text: { primary: "#e2e8f0", secondary: "#94a3b8" },
                divider: "#334155",
              }
            : {}),
        },
        typography: { fontFamily: "Poppins, sans-serif" },
      }),
    [isDark]
  );

  return (
    <ConfigProvider theme={antdConfig}>
      <MuiThemeProvider theme={muiTheme}>{children}</MuiThemeProvider>
    </ConfigProvider>
  );
}

export default AppThemeProviders;
